import React, { useState } from 'react';
import { Box } from '@mui/material';
import { SlInput, useStacklineTheme } from '@stackline/ui';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { SlDialog, SlDialogRoot } from 'src/components/BeaconV2Components/SlDialog';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { BluePlus, DocumentIcon, CheckCircle, CloseIcon, ErrorIcon } from 'src/components/SvgIcons';
import { validateFile, getFileContent } from 'src/components/Search/AdvancedSearch/BulkUpload/BulkUploadDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (items: string[]) => void;
}

const ShelfBulkUploadModal: React.FC<Props> = ({ open, onClose, onSave }) => {
  const theme = useStacklineTheme();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [uploadErrorText, setUploadErrorText] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState<boolean>(false);
  const [input, setInput] = useState<string>('');
  const inputCount = input.split('\n').filter(Boolean).length;

  React.useEffect(() => {
    setInput('');
    setUploadedFile(null);
    setUploadError(false);
  }, [open]);

  // Shelf-specific CSV/text parser: ignore blank rows and delimiter-only rows, take first field, strip quotes and trailing commas
  const parseShelfUploadedItems = async (paste: string, file?: File | null): Promise<string[]> => {
    let raw = paste;
    if (!raw && file) {
      raw = String(await getFileContent(file));
    }
    if (!raw) return [];
    return String(raw)
      .split(/\r?\n/)
      .map((line) => line.replace(/\uFEFF/g, '').trim())
      .map((line) => {
        if (!line) return '';
        const noTrailingCommas = line.replace(/,+$/, '');
        const firstField = (noTrailingCommas.split(',')[0] || '').trim();
        return firstField.replace(/^"|"$/g, '').replace(/^'|'$/g, '').trim();
      })
      .filter((line) => !!line);
  };

  const handleInputChange = (value: string) => {
    setInput(value);
  };

  const handleSave = async () => {
    try {
      setIsParsing(true);
      const items = await parseShelfUploadedItems(input, uploadedFile as any);
      const MAX_IMPORT = 25000;
      const finalItems = Array.isArray(items) && items.length > MAX_IMPORT ? items.slice(0, MAX_IMPORT) : items;
      onSave(finalItems);
      setUploadError(false);
      setUploadErrorText(null);
    } catch (_e) {
      setUploadError(true);
      setUploadErrorText('Failed to parse file. Please check the format.');
    } finally {
      setIsParsing(false);
    }
  };

  const uploadMessage = uploadError
    ? (uploadErrorText || 'The uploaded file does not meet the required specifications. Please try again.')
    : isParsing
      ? 'Parsing file…'
      : 'Upload complete. Your file was successfully uploaded.';

  return (
    <SlDialogRoot>
      <SlDialog onClose={onClose} open={open} width="752px" height="641px">
        <Flex height="100%" flexDirection="column" paddingX={theme.spacing.xl} paddingTop={theme.spacing.mdl} paddingBottom="38px">
          <Text variant="h4">Bulk URL Upload</Text>
          <Box position="absolute" left="699px">
            <button onClick={onClose} style={{ border: 'none', background: 'none' }}>
              <CloseIcon style={{ width: theme.spacing.mdl, height: theme.spacing.mdl }} />
            </button>
          </Box>

          <Flex paddingTop="12px">
            <Text variant="body2">
              Paste a list of Product URLs into the dialog box below or upload a CSV file containing a single list.
            </Text>
          </Flex>
          {/* Removed max input limit note */}

          <Flex paddingTop="22px" flexDirection="column" width="100%">
            <Flex paddingBottom={theme.spacing.md}>
              <Text variant="subtitle1">Paste Product URLs</Text>
            </Flex>
            <SlInput
              width="100%"
              isTextArea
              handleChange={handleInputChange}
              value={input || ''}
              placeholder="Paste Product URLs here"
            />
            <Flex justifyContent="flex-end" paddingTop={theme.spacing.sm}>
              <Text sx={{ fontSize: '10px', fontWeight: 500 }}>{inputCount} Product URLs entered</Text>
            </Flex>

            <Flex flexDirection="column" marginTop={theme.spacing.mdl}>
              <Text variant="subtitle1">Upload CSV file</Text>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                marginTop={theme.spacing.md}
                marginBottom={theme.spacing.mdl}
                border={`1px solid ${theme.colors.primaryGray}`}
                height="160px"
                borderRadius={theme.spacing.sm}
              >
                <button style={{ background: 'none', border: 'none' }}>
                  <label htmlFor="shelf-file-input">
                    {uploadedFile ? (
                      <Flex alignItems="center" gap="sm">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          width="24px"
                          height="24px"
                          borderRadius="50px"
                          sx={{ backgroundColor: theme.colors.primary }}
                        >
                          <DocumentIcon style={{ fill: '#ffffff', width: '18px', height: '18px' }} />
                        </Box>
                        <Text variant="body2">{uploadedFile.name}</Text>
                      </Flex>
                    ) : (
                      <BluePlus role="button" style={{ width: theme.spacing.mdl, height: theme.spacing.mdl }} />
                    )}
                  </label>
                  <input
                    onChange={async (evt) => {
                      const file = (evt.target.files && evt.target.files[0]) || null;
                      if (!file) return;
                      const MAX_BYTES = 8 * 1024 * 1024; // 8MB
                      const errMsg = validateFile(file);
                      if (errMsg) {
                        setUploadError(true);
                        setUploadErrorText(errMsg);
                        return;
                      }
                      if (file.size > MAX_BYTES) {
                        setUploadError(true);
                        setUploadErrorText('File is too large. Please upload a file up to 8MB.');
                        return;
                      }
                      const isCsv = (file.name.split('.').pop() || '').toLowerCase() === 'csv';
                      if (isCsv) {
                        try {
                          const text = String(await getFileContent(file));
                          const lines = text.split(/\r?\n/).slice(0, 200); // sample first 200 lines
                          const csvSplit = (line: string) => line.split(/,(?=(?:[^\"]*\"[^\"]*\")*[^\"]*$)/);
                          const hasMultipleNonEmpty = lines.some((ln) => {
                            if (!ln || !ln.trim()) return false;
                            const cols = csvSplit(ln).map((c) => c.replace(/^\"|\"$/g, '').trim());
                            const nonEmptyCount = cols.filter((c) => c.length > 0).length;
                            return nonEmptyCount > 1;
                          });
                          if (hasMultipleNonEmpty) {
                            setUploadError(true);
                            setUploadErrorText('CSV format invalid. Put all URLs in a single column and leave other columns blank.');
                            return;
                          }
                        } catch (_e) {
                          // If we cannot read/parse, fall back to allowing and let parse step handle errors
                        }
                      }
                      setUploadError(false);
                      setUploadErrorText(null);
                      setUploadedFile(file);
                      // Auto-parse and save immediately upon valid file selection
                      try {
                        setIsParsing(true);
                        const items = await parseShelfUploadedItems('', file as any);
                        const MAX_IMPORT = 25000;
                        const finalItems = Array.isArray(items) && items.length > MAX_IMPORT ? items.slice(0, MAX_IMPORT) : items;
                        if (Array.isArray(finalItems) && finalItems.length > 0) {
                          onSave(finalItems);
                        } else {
                          setUploadError(true);
                          setUploadErrorText('No valid lines found in the file.');
                        }
                      } catch (_e) {
                        setUploadError(true);
                        setUploadErrorText('Failed to parse file. Please check the format.');
                      } finally {
                        setIsParsing(false);
                      }
                    }}
                    style={{ display: 'none' }}
                    id="shelf-file-input"
                    type="file"
                    accept=".csv,.txt"
                  />
                </button>
              </Box>
            </Flex>
          </Flex>
          {(uploadedFile || uploadError || isParsing) && (
            <Flex justifyContent="center" alignItems="center" marginTop={theme.spacing.md} marginBottom={theme.spacing.sm}>
              <Flex gap="xxs" alignItems="center">
                {uploadError ? (
                  <ErrorIcon style={{ width: 18, height: 18 }} />
                ) : uploadedFile ? (
                  <CheckCircle style={{ fill: theme.colors.success, width: '24px' }} />
                ) : null}
                <Text color={uploadError ? 'error' : 'success'} sx={{ marginLeft: .7 }} variant="subtitle3">
                  {uploadMessage}
                </Text>
              </Flex>
            </Flex>
          )}

          <Flex marginTop="auto" alignItems="center" justifyContent="flex-end">
            <Box width="auto">
              <SlButton onClick={handleSave} disabled={(inputCount <= 0 && !uploadedFile) || isParsing} variant="contained">
                Save
              </SlButton>
            </Box>
          </Flex>
        </Flex>
      </SlDialog>
    </SlDialogRoot>
  );
};

export default ShelfBulkUploadModal;


